import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useFocusEffect, useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated';

import { ChallengeFriendCard } from '@/components/games/ChallengeFriendCard';
import { WeeklyChallenges } from '@/components/games/WeeklyChallenges';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Screen } from '@/components/ui/Screen';
import { useThemePalette } from '@/config/theme';
import { useCompetitions } from '@/features/leagues/hooks';
import { AdBanner } from '@/lib/ads/AdBanner';
import { maybeShowInterstitial } from '@/lib/ads/InterstitialAds';

type IoniconName = keyof typeof Ionicons.glyphMap;

/** Synthetic provider_id of the World Cup competition. */
const WC_PROVIDER_ID = 9900001;

function GameCard({
  icon,
  imageUri,
  title,
  body,
  onPress,
  tint,
  index,
}: {
  icon: IoniconName;
  /** When set, the real competition logo is shown instead of the icon. */
  imageUri?: string | null;
  title: string;
  body: string;
  onPress: () => void;
  tint: [string, string];
  index: number;
}) {
  const palette = useThemePalette();
  const reduce = useReducedMotion();
  return (
    <Animated.View entering={reduce ? undefined : FadeInDown.delay(index * 70).springify()}>
      <Pressable accessibilityRole="button" onPress={onPress} className="mx-4 mb-3 active:opacity-90">
        <Card className="overflow-hidden p-0">
          <LinearGradient
            colors={[tint[0], tint[1], 'rgba(255,255,255,0)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />
          <View className="flex-row items-center gap-3.5 px-4 py-4">
            <View className="h-12 w-12 items-center justify-center rounded-2xl bg-volt-glass">
              {imageUri ? (
                <Image
                  source={{ uri: imageUri }}
                  style={{ width: 34, height: 34 }}
                  contentFit="contain"
                  cachePolicy="memory-disk"
                />
              ) : (
                <Ionicons name={icon} size={24} color={palette.voltBright} />
              )}
            </View>
            <View className="flex-1">
              <AppText variant="heading" weight="bold" className="text-ink" numberOfLines={1}>
                {title}
              </AppText>
              <AppText variant="micro" className="mt-0.5 leading-4 text-soft" numberOfLines={2}>
                {body}
              </AppText>
            </View>
            <Ionicons name="chevron-forward" size={18} color={palette.muted} />
          </View>
        </Card>
      </Pressable>
    </Animated.View>
  );
}

/**
 * The flagship "Predict the World Cup" hero - a big, distinct card (not a small
 * GameCard) that headlines the games hub: the WC crest, the contest name, the
 * prize line, and a "pick your champion" hook. Routes into the bracket predictor.
 */
function WorldCupHeroCard({ logoUri, onPress }: { logoUri?: string | null; onPress: () => void }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  return (
    <Animated.View entering={reduce ? undefined : FadeInDown.springify().damping(16)}>
      <Pressable
        accessibilityRole="button"
        onPress={onPress}
        className="mx-4 mb-3 overflow-hidden rounded-card border border-volt-line active:opacity-90"
      >
        <View className="absolute inset-0 bg-glass" />
        <LinearGradient
          colors={['rgba(138,43,226,0.30)', 'rgba(34,211,255,0.12)', 'rgba(255,255,255,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />
        <View className="px-5 pb-4 pt-5">
          {/* Kicker pill */}
          <View className="mb-3 flex-row items-center gap-1.5 self-start rounded-full border border-volt-line bg-volt-glass px-2.5 py-1">
            <Ionicons name="flame" size={12} color={palette.voltBright} />
            <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-volt-bright">
              {t('games.wcHeroKicker')}
            </AppText>
          </View>

          <View className="flex-row items-center gap-4">
            <View className="h-16 w-16 items-center justify-center rounded-2xl bg-volt-glass-strong">
              {logoUri ? (
                <Image
                  source={{ uri: logoUri }}
                  style={{ width: 46, height: 46 }}
                  contentFit="contain"
                  cachePolicy="memory-disk"
                />
              ) : (
                <Ionicons name="trophy" size={32} color={palette.voltBright} />
              )}
            </View>
            <View className="flex-1">
              <AppText variant="title" weight="bold" className="text-ink" numberOfLines={2}>
                {t('games.wcHeroTitle')}
              </AppText>
              <AppText variant="caption" className="mt-1 leading-5 text-soft" numberOfLines={2}>
                {t('games.wcHeroSubtitle')}
              </AppText>
            </View>
          </View>

          {/* Prize + champion hooks */}
          <View className="mt-4 flex-row items-center gap-2">
            <View className="flex-1 flex-row items-center gap-2 rounded-xl border border-volt-line bg-volt-glass px-3 py-2">
              <Ionicons name="gift" size={15} color={palette.captain} />
              <AppText variant="micro" weight="semibold" className="flex-1 text-soft" numberOfLines={2}>
                {t('games.wcHeroPrize')}
              </AppText>
            </View>
            <View className="flex-row items-center gap-1.5 rounded-xl bg-volt px-3.5 py-2.5">
              <AppText variant="caption" weight="bold" style={{ color: palette.onVolt }}>
                {t('games.wcHeroCta')}
              </AppText>
              <Ionicons name="arrow-forward" size={15} color={palette.onVolt} />
            </View>
          </View>
        </View>
      </Pressable>
    </Animated.View>
  );
}

/**
 * Games hub - the home of Tashkeela's prediction games. The flagship World Cup
 * bracket predictor headlines as a hero card; the rest of the games + the
 * cross-game leaderboards follow. Built to grow as more games land.
 */
export default function GamesScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const router = useRouter();
  const competitions = useCompetitions();
  const wcLogo =
    (competitions.data ?? []).find((c) => c.provider_id === WC_PROVIDER_ID)?.logo_url ?? null;

  // A full-screen ad at this natural break — self-capped to one every 3 minutes.
  useFocusEffect(
    useCallback(() => {
      void maybeShowInterstitial();
    }, []),
  );

  return (
    <Screen>
      <View className="px-4 pb-2 pt-2">
        <AppText variant="title">{t('games.title')}</AppText>
      </View>
      <ScrollView contentContainerStyle={{ paddingBottom: 40, paddingTop: 6 }}>
        {/* Flagship: Predict the World Cup — the headline contest. */}
        <WorldCupHeroCard logoUri={wcLogo} onPress={() => router.push('/predictor')} />

        {/* One-tap jump to the daily checklist hub. */}
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/today' as never)}
          className="mx-4 mb-3 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3.5 active:opacity-90"
        >
          <View className="h-10 w-10 items-center justify-center rounded-full bg-volt-glass-strong">
            <Ionicons name="today" size={20} color={palette.voltBright} />
          </View>
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('today.hubCardTitle')}
            </AppText>
            <AppText variant="micro" className="text-soft">
              {t('today.hubCardBody')}
            </AppText>
          </View>
          <Ionicons name="chevron-forward" size={18} color={palette.voltBright} />
        </Pressable>

        {/* Headline 1-tap entry: spin up a 1v1 duel and share the invite link. */}
        <ChallengeFriendCard className="mb-3" />

        {/* Predict & Win — this week's rotating bonus-coin goals. Self-hides when
            the backend has no challenge set yet. */}
        <WeeklyChallenges className="mb-3" />

        {/* Flagship collectible: the Panini-style World Cup sticker album. */}
        <GameCard
          index={0}
          icon="albums"
          title={t('games.albumTitle')}
          body={t('games.albumBody')}
          tint={['rgba(245,200,75,0.20)', 'rgba(214,0,255,0.06)']}
          onPress={() => router.push('/album' as never)}
        />
        <GameCard
          index={1}
          icon="layers"
          title={t('games.accaTitle')}
          body={t('games.accaBody')}
          tint={['rgba(34,211,255,0.22)', 'rgba(95,233,255,0.05)']}
          onPress={() => router.push('/games/acca' as never)}
        />
        <GameCard
          index={2}
          icon="game-controller"
          title={t('games.sportsTitle')}
          body={t('games.sportsBody')}
          tint={['rgba(214,0,255,0.20)', 'rgba(34,211,255,0.06)']}
          onPress={() => router.push('/sports' as never)}
        />
        <GameCard
          index={3}
          icon="football"
          title={t('games.scorePredictTitle')}
          body={t('games.scorePredictBody')}
          tint={['rgba(52,211,153,0.20)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/games/score-predict' as never)}
        />
        <GameCard
          index={4}
          icon="shield-half"
          title={t('games.survivorTitle')}
          body={t('games.survivorBody')}
          tint={['rgba(251,113,133,0.16)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/games/survivor' as never)}
        />
        <GameCard
          index={5}
          icon="flash"
          title={t('games.challengesTitle')}
          body={t('games.challengesBody')}
          tint={['rgba(245,200,75,0.18)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/fantasy/challenges' as never)}
        />
        <GameCard
          index={6}
          icon="shirt"
          title={t('games.totrTitle')}
          body={t('games.totrBody')}
          tint={['rgba(52,211,153,0.18)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/games/team-of-round' as never)}
        />
        <GameCard
          index={8}
          icon="podium"
          title={t('games.qualificationTitle')}
          body={t('games.qualificationBody')}
          tint={['rgba(52,211,153,0.18)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/games/qualification' as never)}
        />
        <GameCard
          index={9}
          icon="football"
          title={t('games.goldenBootTitle')}
          body={t('games.goldenBootBody')}
          tint={['rgba(245,200,75,0.20)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/games/golden-boot' as never)}
        />
        <GameCard
          index={10}
          icon="people"
          title={t('games.friendLeaguesTitle')}
          body={t('games.friendLeaguesBody')}
          tint={['rgba(34,211,255,0.20)', 'rgba(52,211,153,0.06)']}
          onPress={() => router.push('/games/friend-leagues' as never)}
        />
        <GameCard
          index={11}
          icon="git-network"
          title={t('games.bracketLiveTitle')}
          body={t('games.bracketLiveBody')}
          tint={['rgba(138,43,226,0.18)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/games/bracket-live' as never)}
        />
        <GameCard
          index={12}
          icon="gift"
          title={t('games.prizeDrawTitle')}
          body={t('games.prizeDrawBody')}
          tint={['rgba(245,200,75,0.22)', 'rgba(214,0,255,0.06)']}
          onPress={() => router.push('/games/prize-draw' as never)}
        />
        <GameCard
          index={13}
          icon="trophy"
          title={t('games.leaderboardsTitle')}
          body={t('games.leaderboardsBody')}
          tint={['rgba(214,0,255,0.16)', 'rgba(34,211,255,0.05)']}
          onPress={() => router.push('/games/leaderboards-hub')}
        />
        <GameCard
          index={14}
          icon="logo-bitcoin"
          title={t('games.coinsBoardTitle')}
          body={t('games.coinsBoardBody')}
          tint={['rgba(245,200,75,0.20)', 'rgba(214,0,255,0.05)']}
          onPress={() => router.push('/games/coins-leaderboard' as never)}
        />

        <View className="mx-4 mt-3 items-center gap-1.5 rounded-card border border-dashed border-line px-4 py-6">
          <Ionicons name="sparkles-outline" size={20} color={palette.muted} />
          <AppText variant="caption" weight="bold" className="text-soft">
            {t('games.emptyTitle')}
          </AppText>
          <AppText variant="micro" className="text-center text-muted">
            {t('games.emptyBody')}
          </AppText>
        </View>
      </ScrollView>
      <AdBanner />
    </Screen>
  );
}
